import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Autocomplete,
  Button,
  Grid,
  TextInput,
} from '@mantine/core';
import { useState } from 'react';
import DetailsModal from '@/components/DetailsModal';
import validations from '@/helpers/validation';
import { DatePickerInput } from '@mantine/dates';
import agentMutate from '@/api/mutitations/agent';
import s3Mutations from '@/api/mutitations/s3';
import { YES_NO } from '@/helpers/enums';
import Modal from '@/components/Modal/Index';
import { useDisclosure } from '@mantine/hooks';
import PropTypes from 'prop-types';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';

const AgentAdditionalFeeCreate = (props) => {
  const {
    open,
    close,
    opened,
    setKey,
    id,
  } = props;

  const t = prepareTranslate();
  const { useUpload } = s3Mutations;
  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const { mutateAsync: uploadMutate } = useUpload();

  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');

  const form = useForm({
    initialValues: {
      files: [],
    },
    validate: {
      value: (value) => validations.required(value),
      type: (value) => validations.required(value),
      date: (value) => validations.required(value),
    },
  });

  const { mutateAsync: addFeesMutate } = agentMutate.usePushToArray();

  const handleFeesSave = async (values) => {
    setLoading(true);
    let uploadFiles;

    try {
      const clonedValues = { ...values };
      const { files } = values;

      if (files.length > 0) {
        const formData = new FormData();

        files.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          uploadFiles = await uploadMutate(formData);
          clonedValues.files = uploadFiles.files;
        } catch (error) {
          setResultLabel(t('pages.agentEdit.notification.failure.title'));
          actionResultOpen();
          return;
        }
      }

      clonedValues.isPaid = clonedValues.isPaid === t('global.yes');

      const data = {
        value: { ...clonedValues },
        id,
        key: 'additionalFees',
      };

      const newFees = await addFeesMutate(data);

      if (!newFees) {
        setResultLabel(t('pages.agentEdit.notification.failure.title'));
        actionResultOpen();
        return;
      }

      setResultLabel(t('components.createAgentAdditionalFee.sucess'));
      actionResultOpen();
      form.reset();
      setKey((prev) => prev + 1);
      close();
    } catch (error) {
      setResultLabel(t('pages.agentEdit.notification.failure.title'));
      actionResultOpen();
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    form.reset();
    form.clearErrors();
    close();
  };

  const actions = [
        <Button
            key='primary'
            loading={loading}
            onClick={form.onSubmit(handleFeesSave)}
            w={'7rem'}
        >
            {t('global.update')}
        </Button>,
        <Button
            key='cancel'
            variant='outline'
            onClick={handleCancelClick}
            w={'7rem'}>
            {t('global.cancel')}
        </Button>,
  ];

  const resultActions = [
        <Button
            key='clseButton' onClick={() => actionResultClose()}>
            {t('global.close')}
        </Button>,
  ];

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
  };

  return (
        <>
            <DetailsModal
                actions={actions}
                open={open}
                close={handleCancelClick}
                opened={opened}
                label={t('components.createAgentAdditionalFee.title')}
            >
                <Grid gutter={{ base: 'xl' }}>
                    <Grid.Col span={{ base: 3 }}>
                        <TextInput
                            label={t('components.createAgentAdditionalFee.type')}
                            required
                            key={form.key('type')}
                            {...form.getInputProps('type')}
                        />
                        <Autocomplete
                            label={t('components.createAgentAdditionalFee.isPaid')}
                            data={YES_NO}
                            required
                            key={form.key('isPaid')}
                            {...form.getInputProps('isPaid')}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 3 }}>
                        <TextInput
                            type='number'
                            autoComplete='off'
                            label={t('components.createAgentAdditionalFee.sum')}
                            key={form.key('sum')}
                            maxLength={7}
                            required
                            {...form.getInputProps('value')}
                        />
                        <DatePickerInput
                            valueFormat="DD/MM/YYYY"
                            key={'payDate'}
                            label={t('components.createAgentAdditionalFee.payDate')}
                            {...form.getInputProps('payDate')}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 3 }}>
                        <DatePickerInput
                            required
                            valueFormat="DD/MM/YYYY"
                            key={'date'}
                            label={t('components.createAgentAdditionalFee.date')}
                            {...form.getInputProps('date')}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 3 }}>
                        <TextInput
                            key={'comments'}
                            label={t('components.createAgentAdditionalFee.comments')}
                            {...form.getInputProps('comments')}
                        />
                    </Grid.Col>
              </Grid>
              <UploadFilesButton
                    onFilesSelected={onFileSelected}
                    label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
              />
                {form.values.files?.length > 0 && (
                    <EditFiles
                        files={form.values.files}
                        onNameChange={onFileNameChange}
                        onRemove={onDeleteFile}
                    />
                )}
            </DetailsModal>
            <Modal
                opened={actionResultOpended}
                onClose={actionResultClose}
                actions={resultActions}
                label={resultLabel}
            />
        </>
  );
};

AgentAdditionalFeeCreate.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
  id: PropTypes.string,
};

export default AgentAdditionalFeeCreate;
